/** null = authenticated home/utility only. Everything else must be assigned on the role or user. */
export type ModuleRequirement = string | string[] | null;

/**
 * Maps each frontend route to the permission module granted under
 * Settings → Roles / Users. Keep in sync with backend MODULE_MAP.
 */
export const ROUTE_MODULE: Record<string, ModuleRequirement> = {
  '/dashboard': null,
  '/notifications': null,
  '/executive-dashboard': 'REPORTS',
  '/patients': 'REGISTRATION',
  '/appointments': ['APPOINTMENTS', 'REGISTRATION', 'OPD'],
  '/crm': 'CRM',
  '/opd': 'OPD',
  '/doctor/dashboard': 'OPD',
  '/doctor/emr': 'EMR',
  '/doctor-schedule': 'OPD',
  '/cpoe': 'EMR',
  '/ipd': 'IPD',
  '/bed-board': 'IPD',
  '/nursing': 'IPD',
  '/indents': 'INVENTORY',
  '/emar': 'IPD',
  '/icu': 'IPD',
  '/emergency': 'EMERGENCY',
  '/ot': 'OT',
  '/ophthalmology': 'OPD',
  '/discharge-summary': 'IPD',
  '/consents': 'CONSENT',
  '/patient-portal': ['PATIENT_PORTAL', 'REGISTRATION'],
  '/laboratory': 'LAB',
  '/lab/parameter-master': 'LAB',
  '/lab/reference-ranges': 'LAB',
  '/lab/culture-templates': 'LAB',
  '/lab/pathology': 'LAB',
  '/lab/report-master': 'LAB',
  '/radiology': 'RADIOLOGY',
  '/bloodbank': 'BLOOD_BANK',
  '/pharmacy': 'PHARMACY',
  '/terminology': 'LAB',
  '/billing': 'BILLING',
  '/front-office-billing': 'BILLING',
  '/insurance': 'INSURANCE',
  '/insurance-preauth': 'INSURANCE',
  '/corporate': 'BILLING',
  '/payers': 'INSURANCE',
  '/finance': 'FINANCE',
  '/inventory': 'INVENTORY',
  '/vendors': 'INVENTORY',
  '/diet': 'IPD',
  '/biomedical': 'BIOMEDICAL',
  '/cssd': ['CSSD', 'INVENTORY'],
  '/communications': 'COMMUNICATIONS',
  '/hrms': 'HR',
  '/roster': 'HR',
  '/workflow': 'WORKFLOW',
  '/approvals': 'APPROVALS',
  '/dms': 'DMS',
  '/quality': 'QUALITY',
  '/nabh': ['NABH', 'REPORTS'],
  '/audit': 'ADMIN',
  '/audit-log': 'ADMIN',
  '/abdm': ['ABDM', 'REGISTRATION'],
  '/fhir': 'FHIR',
  '/telemedicine': ['TELEMEDICINE', 'OPD'],
  '/settings': 'ADMIN',
  '/settings/branches': 'ADMIN',
  '/settings/user-stations': 'ADMIN',
  '/masters': 'ADMIN',
  '/system-masters': 'ADMIN',
  '/location-master': 'ADMIN',
  '/charge-master': 'BILLING',
  '/reports': 'REPORTS',
  '/analytics': 'REPORTS',
};

export function moduleForPath(pathname: string): ModuleRequirement {
  const keys = Object.keys(ROUTE_MODULE).sort((a, b) => b.length - a.length);
  const match = keys.find((k) => pathname === k || pathname.startsWith(`${k}/`));
  return match ? ROUTE_MODULE[match] : 'ADMIN';
}

export function hasAssignedModule(
  accessible: string[] | '*' | undefined | null,
  required: ModuleRequirement,
): boolean {
  if (required == null) return true;
  if (accessible === '*') return true;
  if (!Array.isArray(accessible) || !accessible.length) return false;
  const needed = Array.isArray(required) ? required : [required];
  return needed.some((m) => accessible.includes(m));
}

export function readStoredModules(): string[] | '*' | null {
  try {
    const stored = localStorage.getItem('accessibleModules');
    if (stored === '*') return '*';
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}
